import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Member } from '@/features/iam/members/Member'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * View model for the dialog shown when someone else saved the legend since it
 * was loaded: it forces our changes, or keeps theirs.
 */
export class ConflictDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  private _force: () => Promise<unknown> = () => Promise.resolve()

  @observable.ref accessor lastUpdatedBy = Member.none

  /**
   * @param _reload Loads the latest version, with whichever changes won.
   * @param _createAlertErrorHandler
   */
  constructor(
    private readonly _reload: () => Promise<unknown>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Asks how to resolve the conflict, and resolves it.
   *
   * @param lastUpdatedBy Whoever saved the legend.
   * @param force Saves our latest changes over theirs.
   * @returns Whether our changes were forced.
   */
  async resolve(lastUpdatedBy: Member, force: () => Promise<unknown>) {
    this._force = force
    const response = this._prompt.prompt()
    runInAction(() => {
      this.lastUpdatedBy = lastUpdatedBy
      this.modal.show()
    })
    return (await response) ?? false
  }

  /**
   * Saves our changes over theirs.
   */
  force = task.resolved(() => this._respond(true))

  /**
   * Drops our changes and loads theirs.
   */
  keep = task.resolved(() => this._respond(false))

  /**
   * When saving or loading fails, the dialog stays open, and the alert
   * reports why.
   */
  private async _respond(forced: boolean) {
    try {
      if (forced) {
        await this._force()
      }
      await this._reload().catch(
        this._createAlertErrorHandler('Loading the legend failed'),
      )
    } catch {
      return
    }
    this._prompt.respond(forced)
    this.modal.close()
  }
}
